import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Images, Play, X } from "lucide-react";
import { api } from "@/lib/api";
import { GALLERY } from "@/content";

const FILTERS = [
  { id: "all", label: "બધા" },
  { id: "uploads", label: "નવા અપલોડ" },
  { id: "sports", label: "સ્પોર્ટ્સ" },
  { id: "events", label: "ઇવેન્ટ્સ" },
  { id: "medical", label: "મેડિકલ કેમ્પ" },
  { id: "aid", label: "સહાય વિતરણ" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    api
      .getMedia()
      .then((list) => {
        if (Array.isArray(list)) {
          setUploaded(
            list.map((m) => ({
              src: api.mediaFileUrl(m.id),
              caption: m.caption || "ટ્રસ્ટ પ્રવૃત્તિ",
              cat: "uploads",
              kind: m.kind,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const items = useMemo(() => {
    if (filter === "uploads") return uploaded;
    if (filter === "all") return [...uploaded, ...GALLERY];
    return GALLERY.filter((g) => g.cat === filter);
  }, [filter, uploaded]);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 lg:py-24 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-10"
        >
          <p className="font-head font-bold text-sm tracking-[0.2em] text-terracotta mb-4 flex items-center gap-2">
            <Images className="w-4 h-4" />
            અમારી ઝલક
          </p>
          <h2 className="font-display text-ink text-4xl sm:text-5xl leading-snug">
            સેવાના <span className="text-leaf">ક્ષણો</span>
          </h2>
          <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
            કેમ્પ, સ્પર્ધા, યાત્રા અને વિતરણ — અમારી પ્રવૃત્તિઓના સાચા ફોટા.
          </p>
        </motion.div>

        <div data-testid="gallery-filters" className="flex flex-wrap gap-2 mb-8">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`gallery-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-5 py-2.5 font-head font-bold text-sm transition-all duration-200 ${
                filter === f.id
                  ? "bg-leaf text-cream shadow-md"
                  : "bg-white border border-stone-200 text-ink/60 hover:text-ink hover:border-leaf/40"
              }`}
            >
              {f.label}
              {f.id === "uploads" && uploaded.length > 0 ? ` (${uploaded.length})` : ""}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.button
                layout
                key={g.src}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(g)}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.3) }}
                className="group relative overflow-hidden rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] text-left"
              >
                {g.kind === "video" ? (
                  <>
                    <video
                      src={g.src}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-48 sm:h-56 object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-ink/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-saffron transition-colors duration-200">
                        <Play className="w-5 h-5 text-cream ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </>
                ) : (
                  <img
                    src={g.src}
                    alt={g.caption}
                    loading="lazy"
                    className="w-full h-48 sm:h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-3 pt-8">
                  <span className="font-head font-semibold text-cream text-xs sm:text-sm">
                    {g.caption}
                  </span>
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-ink/90 backdrop-blur-sm flex flex-col items-center justify-center p-5"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-cream hover:bg-terracotta transition-colors"
              aria-label="બંધ કરો"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full"
            >
              {active.kind === "video" ? (
                <video
                  src={active.src}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh] rounded-2xl shadow-2xl bg-black"
                />
              ) : (
                <img
                  src={active.src}
                  alt={active.caption}
                  className="max-w-full max-h-[80vh] rounded-2xl shadow-2xl object-contain bg-white"
                />
              )}
            </motion.div>
            <p className="mt-4 font-head font-semibold text-cream/90 text-sm">
              {active.caption}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
